import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../store/auth';
import { usePhoneStore } from '../store/phone';
import { installMockApi } from './_mockApi';
import { endCall } from './_mockCdr';
import { mockEnabled } from './_mockEnv';
import { installPhoneSim, installRingHelpers } from './_phoneSim';
import { closeVisibleModal, demoSteps, waitMs, type DemoAlive } from './_demoSteps';
import CallWorkbenchPrototype from './CallWorkbenchPrototype';

/**
 * DEV 专用界面仿真（生产构建不生效）：仅当 pnpm dev 且 URL 带 ?mock=1（或 localStorage bcc_devmock=1）。
 *
 * 用途：本机没有 FreeSWITCH/测试后端时，对「坐席顶栏通话条 + 服务小结」做界面级走查——
 * - axios 层 mock：见 `_mockApi.ts`（未覆盖端点统一回落空数据，避免全局错误弹 Toast）。
 * - 软电话动作替换：见 `_phoneSim.ts`，供真实按钮（接听/拒接/挂断/转接面板）无话机可用地完整走一遍交互。
 * - 右下角仿真面板：快捷步骤（点击直达 空闲/来电/接通/转接面板/号码名/服务小结/外呼），
 *   手动动作（呼入-两种弹屏/外呼/复位）与可选「循环演示」6s/步；?mock=1 即启用。
 *
 * 不做的事：不 mock /ws 管理台（断线静默重连即可）、不 mock SIP WS（register 已替换）。
 */

// —— 模块级一次性生效 ——
if (mockEnabled) {
  const auth = {
    token: 'dev-mock-token',
    user: { id: 1, username: 'agent1001', name: '测试坐席', role: 'agent', extension: '1001', agentId: 7, skill_group_id: 1 },
  };
  try {
    window.localStorage.setItem('bcc-auth', JSON.stringify({ state: auth, version: 0 }));
  } catch {
    /* 忽略 */
  }
  useAuthStore.setState({ token: auth.token, user: auth.user as any });
  installMockApi();
  installPhoneSim();
  installRingHelpers();
}

/** 仿真状态条（右下角悬浮，仅 mock 模式渲染） */
export default function PhoneMockGate() {
  // hooks 无条件调用（保持 hook 顺序）；未启用时渲染空
  const st = usePhoneStore();
  const [demoOn, setDemoOn] = useState(false);
  const [demoLabel, setDemoLabel] = useState('');
  const [runIdx, setRunIdx] = useState(-1);
  const [panelPos, setPanelPos] = useState<{ left: number; top: number } | null>(null);
  const dragRef = useRef<{ offsetX: number; offsetY: number } | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const tokenRef = useRef(0);
  useEffect(() => () => void tokenRef.current++, []); // 卸载即停演示

  const startPanelDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    setPanelPos({ left: rect.left, top: rect.top });
    dragRef.current = { offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const movePanel = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const panel = panelRef.current;
    if (!panel) return;
    const maxLeft = Math.max(0, window.innerWidth - panel.offsetWidth);
    const maxTop = Math.max(0, window.innerHeight - panel.offsetHeight);
    setPanelPos({
      left: Math.min(maxLeft, Math.max(0, e.clientX - dragRef.current.offsetX)),
      top: Math.min(maxTop, Math.max(0, e.clientY - dragRef.current.offsetY)),
    });
  };
  const stopPanelDrag = () => { dragRef.current = null; };

  const stepDefs = demoSteps();

  /** 快捷步骤只控制电话仿真面板；不结束真实通话，避免会议讨论时弹出服务小结。 */
  const prepareJump = () => {
    tokenRef.current++;
    closeVisibleModal();
  };

  /** 单步直达：取消循环/进行中的步骤，跳转到第 i 步并完整演一遍（不自动续播） */
  const runSingle = async (i: number) => {
    prepareJump();
    const tok = ++tokenRef.current;
    setDemoOn(false);
    setDemoLabel('');
    setRunIdx(i);
    const step = stepDefs[i];
    try {
      await step.run(() => tokenRef.current === tok);
    } catch (e) {
      console.warn('[devsim] step error', e);
    }
    if (tokenRef.current === tok) setRunIdx(-1);
  };

  const startDemo = () => {
    if (demoOn) return;
    prepareJump();
    const tok = ++tokenRef.current;
    setRunIdx(-1);
    setDemoOn(true);
    void runDemo(tok);
  };
  const stopDemo = () => {
    tokenRef.current++;
    setDemoOn(false);
    setDemoLabel('');
    setRunIdx(-1);
    (window as any).__phoneSim?.reset();
    closeVisibleModal();
  };

  async function runDemo(tok: number) {
    const alive: DemoAlive = () => tokenRef.current === tok;
    const steps = stepDefs;
    for (let i = 0; ; i++) {
      if (!alive()) return;
      const step = steps[i % steps.length];
      setDemoLabel(step.label);
      const t0 = Date.now();
      try {
        await step.run(alive);
      } catch (e) {
        console.warn('[devsim] demo step error', e);
      }
      if (!alive()) return;
      // 每步展示约 6 秒（步内动作结束后的余量停留）
      const left = 6000 - (Date.now() - t0);
      if (left > 0) await waitMs(left, alive);
    }
  }

  if (!mockEnabled || window.location.pathname !== '/workbench') return null;
  const stepShort = ['空闲', '来电', '接通', '转接面板', '号码名', '服务小结', '外呼'];
  return (
    <>
      <CallWorkbenchPrototype />
      <div
      ref={panelRef}
      onPointerMove={movePanel}
      onPointerUp={stopPanelDrag}
      onPointerCancel={stopPanelDrag}
      style={{
        position: 'fixed',
        ...(panelPos ? { left: panelPos.left, top: panelPos.top } : { right: 16, bottom: 16 }),
        zIndex: 99999,
        width: 260,
        padding: '10px 12px',
        background: 'rgba(17,24,39,0.92)',
        border: '1px solid rgba(255,255,255,0.25)',
        borderRadius: 10,
        color: '#e5e7eb',
        fontSize: 12,
        lineHeight: 1.5,
        boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
        userSelect: 'none',
      }}
    >
      <div
        onPointerDown={startPanelDrag}
        style={{ fontWeight: 700, marginBottom: 6, color: '#7dd3fc', cursor: 'move', touchAction: 'none' }}
        title="拖动此标题栏移动面板"
      >
        PRD原型仿真 <span style={{ fontWeight: 400, color: '#9ca3af' }}>（mock=1）</span>
      </div>
      {/* 快捷直达：点某一步立即播到该状态（不再等循环） */}
      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>快捷步骤（点击直达）</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {stepShort.map((s, i) => (
          <button
            key={i}
            id={`sim-jump-${i}`}
            onClick={() => void runSingle(i)}
            style={{
              padding: '2px 7px',
              borderRadius: 10,
              border: runIdx === i ? '1px solid #22d3ee' : '1px solid #334155',
              background: runIdx === i ? 'rgba(34,211,238,0.16)' : '#1e293b',
              color: runIdx === i ? '#a5f3fc' : '#cbd5e1',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            {i + 1}.{s}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 11, color: '#9ca3af', margin: '6px 0 4px' }}>手动动作</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <SimBtn id="sim-ring-name" onClick={() => (window as any).__phoneSim.ringInName()}>
          呼入·客户名
        </SimBtn>
        <SimBtn id="sim-ring-num" onClick={() => (window as any).__phoneSim.ringInNumberName()}>
          呼入·号码即名
        </SimBtn>
        <SimBtn id="sim-ring-out" onClick={() => (window as any).__phoneSim.ringOut()}>
          外呼
        </SimBtn>
        <SimBtn id="sim-reset" onClick={() => (window as any).__phoneSim.reset()}>
          复位
        </SimBtn>
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <button
          id="sim-demo-start"
          onClick={demoOn ? stopDemo : startDemo}
          style={{
            flex: 1,
            padding: '4px 10px',
            borderRadius: 6,
            border: demoOn ? '1px solid #f59e0b' : '1px solid #2563eb',
            background: demoOn ? 'rgba(245,158,11,0.18)' : 'rgba(37,99,235,0.25)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {demoOn ? '■ 停止演示' : '▶ 循环演示（6 秒/步）'}
        </button>
      </div>
      {(demoOn || runIdx >= 0) && (
        <div id="sim-demo-label" style={{ marginTop: 6, color: '#fbbf24', lineHeight: 1.4 }}>
          {demoOn ? `循环演示中：${demoLabel}` : runIdx >= 0 ? `正在执行：${stepShort[runIdx]}（完毕即停）` : ''}
        </div>
      )}
      <div id="sim-status" style={{ marginTop: demoOn || runIdx >= 0 ? 4 : 8, fontFamily: 'Consolas, monospace', color: '#d1d5db', wordBreak: 'break-all', whiteSpace: 'pre-line' }}>
        {`status=${st.status}\nremote=${st.remoteNumber || '—'}\n客户=${st.customer?.name || '—'}${st.transferredOut ? '\ntransferredOut=true' : ''}${st.consultPhase ? `\nconsult=${st.consultPhase} ${st.consultDest || ''}` : ''}`}
      </div>
      <div style={{ marginTop: 4, color: '#6b7280' }}>顶栏真实按钮操作：接听/拒接/挂断/转接/邀请（数据为假，界面为真）</div>
      </div>
    </>
  );
}

function SimBtn(props: { id: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      id={props.id}
      onClick={props.onClick}
      style={{
        padding: '3px 10px',
        borderRadius: 6,
        border: '1px solid #4b5563',
        background: '#1f2937',
        color: '#e5e7eb',
        fontSize: 12,
        cursor: 'pointer',
      }}
    >
      {props.children}
    </button>
  );
}
